import { useCallback, useEffect, useState } from "react";
import { AuthenticationGroupDetails, useCoastguard } from "@cofano/coastguard2-frontend-client";
import { Box, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useStoreActions, useStoreState } from "ezStore";
import { Button } from "@cofano/cck";
import { CircularProgress } from "@mui/material";
import UpdateScreen from "./UpdateScreen";
import CoastguardSessionSelect from "components/layout/coastguard/CoastguardSessionSelect";
import { intl } from "LocaleWrapper";
import useCckContext from "@cofano/cck/hooks/useCckContext";

const Login = ({ latestVersion }: { latestVersion?: string }) => {
	const session = useStoreState(store => store.session);

	const { setVersion } = useStoreActions(store => store.session);

	const { instance: { isAuthenticationLoading } } = useCoastguard();

	const shouldUpdate = session.version ? session.version !== latestVersion : false;

	useEffect(() => {
		if (latestVersion) {
			setVersion(latestVersion);
		}
	}, [ latestVersion ]);

	return shouldUpdate ? (
		<UpdateScreen version={latestVersion ?? ""} />
	) : isAuthenticationLoading ? (
		<Paper
			square
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				width: "100%",
				height: "100%",
			}}
		>
			<CircularProgress size={30} />
		</Paper>
	) : (
		<LoginPage />
	);
};

const LoginPage = () => {
	const { instance: {
		getAuthenticationGroupDetails,
		switchActiveSession,
		logoutSession,
		logoutAuthenticationGroupSessions,
		getAuthorizeApplicationRedirect,
	} } = useCoastguard();

	const location = useLocation();
	const navigate = useNavigate();
	const isMobile = useMediaQuery(useTheme().breakpoints.down("md"));

	const [ authenticationGroupDetails, setAuthenticationGroupDetails ] = useState<AuthenticationGroupDetails | null>(null);

	const { darkModeEnabled, enableDarkMode } = useCckContext();

	const refetchAuthenticationGroupDetails = useCallback(() => {
		getAuthenticationGroupDetails().then(result => {
			if (result != null && result.sessions != null && result.sessions.length > 1) {
				setAuthenticationGroupDetails(result);
			} else {
				getAuthorizeApplicationRedirect().then(res => {
					window.location.href = res;
				});
			}
		}).catch(() => {
			getAuthorizeApplicationRedirect().then(res => {
				window.location.href = res;
			});
		});
	}, [ getAuthenticationGroupDetails, setAuthenticationGroupDetails, getAuthorizeApplicationRedirect ]);

	useEffect(() => {
		refetchAuthenticationGroupDetails();
	}, [ refetchAuthenticationGroupDetails ]);

	return (
		<Paper
			square
			sx={{
				backgroundImage: "url('/customer/background.jpg')",
				backgroundSize: "cover",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				position: "fixed",
				inset: 0,
			}}
		>
			<Paper sx={{
				display: "flex",
				alignItems: "center",
				flexDirection: "column",
				gap: 2,
				padding: 2,
				maxWidth: isMobile ? "100%" : "50vw",
				maxHeight: isMobile ? "100%" : "80vh",
			}}>
				<img
					alt="logo"
					src="/logo.png"
					style={{
						maxHeight: "20vh",
						maxWidth: "100%",
						objectFit: "contain",
					}}
				/>

				<Typography variant="h5" component="span" textAlign="center">
					{intl.formatMessage({
						id: "COASTGUARD_APPLICATION_SESSIONS",
						defaultMessage: "Coastguard Application Sessions",
					})}
				</Typography>

				<Typography variant="body1" component="span" textAlign="center">
					{intl.formatMessage(
						{
							id: "COASTGUARD_SELECT_ACTIVE_SESSION",
							defaultMessage: "Select an active session to continue to {application}",
						},
						{
							application: (
								<b>{authenticationGroupDetails?.application.name ?? intl.formatMessage({ id: "APPLICATION", defaultMessage: "application" })}</b>
							),
						},
					)}
				</Typography>

				{authenticationGroupDetails != null && (
					<Box
						width="100%"
						flex="1 1 auto"
						minHeight={0}
						display="flex"
						flexDirection="column"
					>
						<CoastguardSessionSelect
							sessions={authenticationGroupDetails.sessions}
							currentSessionUuid={null}
							switchSession={sessionUuid => {
								switchActiveSession(sessionUuid)
									.then(() => {
										navigate(location.state?.redirectLocation ?? "/");
									})
									.catch(() => refetchAuthenticationGroupDetails());
							}}
							logoutSession={sessionUuid => {
								logoutSession(sessionUuid)
									.finally(() => refetchAuthenticationGroupDetails());
							}}
							hideSingleAccountDetails={false}
						/>
					</Box>
				)}

				<Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
					<Button
						variant="outlined"
						size="small"
						onClick={() => {
							getAuthorizeApplicationRedirect().then((res) => {
								window.location.href = res;
							}).finally(() => refetchAuthenticationGroupDetails());
						}}
					>
						{intl.formatMessage({ id: "COASTGUARD_ADD_ANOTHER_SESSION", defaultMessage: "Add another session" })}
					</Button>

					<Button
						variant="outlined"
						size="small"
						onClick={() => {
							logoutAuthenticationGroupSessions()
								.finally(() => refetchAuthenticationGroupDetails());
						}}
					>
						{intl.formatMessage({ id: "COASTGUARD_END_ALL_SESSIONS", defaultMessage: "End all sessions" })}
					</Button>
				</Box>

				<Button
					variant="text"
					size="small"
					icon={darkModeEnabled ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
					onClick={() => enableDarkMode(!darkModeEnabled)}
				>
					{darkModeEnabled ? (
						intl.formatMessage({ id: "DISABLE_DARK_MODE", defaultMessage: "Turn on the lights" })
					) : (
						intl.formatMessage({ id: "ENABLE_DARK_MODE", defaultMessage: "Turn off the lights" })
					)}
				</Button>
			</Paper>
		</Paper>
	);
};

export default Login;
